import React from 'react';
import { Timeline, Tag, Typography, Descriptions, Collapse, Space, Row, Col } from 'antd';
import { SampleAnalysis } from '../../../../../types/lab/SampleTypes';
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	ExclamationCircleOutlined,
	PlayCircleOutlined,
} from '@ant-design/icons';
import { SectionCard, StatusTag, EmptyState } from '../../../../../components/shared';
import dayjs from 'dayjs';

const { Text } = Typography;

interface AnalysisResultsTabProps {
	analyses: SampleAnalysis[];
}

const AnalysisResultsTab: React.FC<AnalysisResultsTabProps> = ({ analyses }) => {
	const getStatusIcon = (status: string) => {
		switch (status?.toLowerCase()) {
			case 'completed': return <CheckCircleOutlined style={{ color: 'var(--mg-success)' }} />;
			case 'in_progress': case 'running': return <PlayCircleOutlined style={{ color: 'var(--mg-primary)' }} />;
			case 'failed': return <ExclamationCircleOutlined style={{ color: 'var(--mg-danger)' }} />;
			default: return <ClockCircleOutlined style={{ color: 'var(--mg-text-disabled)' }} />;
		}
	};

	const getStepBorderColor = (status: string): string => {
		switch (status?.toLowerCase()) {
			case 'completed': return 'var(--mg-success)';
			case 'in_progress': return 'var(--mg-primary)';
			default: return 'var(--mg-warning)';
		}
	};

	const formatDate = (dateString: string | null | undefined) => {
		if (!dateString) return 'N/A';
		return dayjs(dateString).format('MMM DD, YYYY HH:mm');
	};

	const calculateDuration = (start: string | undefined, end: string | undefined) => {
		if (!start || !end) return 'N/A';
		const duration = dayjs(end).diff(dayjs(start), 'minute');
		if (duration < 60) return `${duration}m`;
		const hours = Math.floor(duration / 60);
		const minutes = duration % 60;
		return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
	};

	if (!analyses || analyses.length === 0) {
		return <EmptyState title="No analysis results" description="No analysis results found for this sample" />;
	}

	const smText: React.CSSProperties = { fontSize: 12 };
	const smLabel: React.CSSProperties = { fontSize: 12, fontWeight: 500 };
	const secondaryText: React.CSSProperties = { fontSize: 12, color: 'var(--mg-text-secondary)' };

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Analysis Overview */}
			<SectionCard title="Analysis Overview">
				<Row gutter={[16, 16]}>
					<Col span={6} style={{ textAlign: 'center' }}>
						<Text style={secondaryText}>Total Analyses</Text>
						<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-text)' }}>{analyses.length}</div>
					</Col>
					<Col span={6} style={{ textAlign: 'center' }}>
						<Text style={secondaryText}>Completed</Text>
						<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-success)' }}>
							{analyses.filter(a => a.status === 'completed').length}
						</div>
					</Col>
					<Col span={6} style={{ textAlign: 'center' }}>
						<Text style={secondaryText}>In Progress</Text>
						<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-primary)' }}>
							{analyses.filter(a => a.status === 'in_progress' || a.status === 'running').length}
						</div>
					</Col>
					<Col span={6} style={{ textAlign: 'center' }}>
						<Text style={secondaryText}>Failed</Text>
						<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-danger)' }}>
							{analyses.filter(a => a.status === 'failed').length}
						</div>
					</Col>
				</Row>
			</SectionCard>

			{/* Analysis Timeline */}
			<SectionCard title="Analysis Timeline">
				<Timeline mode="left" style={{ paddingTop: 8 }}>
					{analyses.map((analysis) => (
						<Timeline.Item key={analysis.id} dot={getStatusIcon(analysis.status)}>
							<div>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
									<div>
										<Text strong style={{ fontSize: 13 }}>{analysis.protocol_name}</Text>
										<br />
										<Text style={secondaryText}>Analysis ID: {analysis.analysis_id}</Text>
										<br />
										<Text style={secondaryText}>Version: {analysis.protocol_version}</Text>
									</div>
									<StatusTag status={analysis.status} />
								</div>

								<Descriptions size="small" column={2} colon={false}>
									<Descriptions.Item label={<Text style={smText}>Assigned</Text>}>
										<Text style={smText}>{formatDate(analysis.assigned_at)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smText}>Started</Text>}>
										<Text style={smText}>{formatDate(analysis.started_at)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smText}>Completed</Text>}>
										<Text style={smText}>{formatDate(analysis.completed_at)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smText}>Duration</Text>}>
										<Text style={smText}>{calculateDuration(analysis.started_at, analysis.completed_at)}</Text>
									</Descriptions.Item>
								</Descriptions>

								{analysis.notes && (
									<div style={{ marginTop: 8 }}>
										<Text style={secondaryText}>{analysis.notes}</Text>
									</div>
								)}
							</div>
						</Timeline.Item>
					))}
				</Timeline>
			</SectionCard>

			{/* Detailed Analysis Information */}
			<SectionCard title="Analysis Details">
				<Collapse size="small" ghost>
					{analyses.map((analysis) => (
						<Collapse.Panel
							key={analysis.id}
							header={
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
									<Space>
										{getStatusIcon(analysis.status)}
										<Text style={{ fontSize: 13, fontWeight: 500 }}>{analysis.protocol_name}</Text>
										<Text style={secondaryText}>v{analysis.protocol_version}</Text>
									</Space>
									<StatusTag status={analysis.status} />
								</div>
							}
						>
							<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
								{/* Analysis Metadata */}
								<Descriptions size="small" column={3} colon={false} bordered>
									<Descriptions.Item label={<Text style={smLabel}>Analysis ID</Text>}>
										<Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{analysis.analysis_id}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smLabel}>Sample ID</Text>}>
										<Text style={smText}>{analysis.sample_id}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smLabel}>Client</Text>}>
										<Text style={smText}>{analysis.client}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smLabel}>Assigned At</Text>}>
										<Text style={smText}>{formatDate(analysis.assigned_at)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smLabel}>Started At</Text>}>
										<Text style={smText}>{formatDate(analysis.started_at)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={smLabel}>Completed At</Text>}>
										<Text style={smText}>{formatDate(analysis.completed_at)}</Text>
									</Descriptions.Item>
								</Descriptions>

								{/* Analysis Results */}
								{analysis.results && (
									<div>
										<Text style={{ fontSize: 13, fontWeight: 500 }}>Results Data</Text>
										<SectionCard style={{ marginTop: 8, background: 'var(--mg-bg-subtle)' }}>
											<pre style={{ fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
												{JSON.stringify(analysis.results, null, 2)}
											</pre>
										</SectionCard>
									</div>
								)}

								{/* Protocol Instances for this Analysis */}
								{(analysis as any).protocolInstances?.length > 0 && (
									<div>
										<Text style={{ fontSize: 13, fontWeight: 500 }}>Protocol Execution</Text>
										<div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
											{(analysis as any).protocolInstances.map((instance: any) => (
												<SectionCard key={instance.id}>
													<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
														<div>
															<Text style={{ fontSize: 13, fontWeight: 500 }}>{instance.protocol?.name}</Text>
															<br />
															<Text style={secondaryText}>
																Group: {instance.group_id} • Protocol ID: {instance.protocol_id}
															</Text>
															<br />
															<Text style={secondaryText}>{instance.protocol?.description}</Text>
														</div>
														<StatusTag status={instance.status} />
													</div>

													<Descriptions size="small" column={3} colon={false} style={{ marginBottom: 12 }}>
														<Descriptions.Item label={<Text style={smText}>Started</Text>}>
															<Text style={smText}>{formatDate(instance.started_at)}</Text>
														</Descriptions.Item>
														<Descriptions.Item label={<Text style={smText}>Completed</Text>}>
															<Text style={smText}>{formatDate(instance.completed_at)}</Text>
														</Descriptions.Item>
														<Descriptions.Item label={<Text style={smText}>Duration</Text>}>
															<Text style={smText}>
																{instance.started_at && instance.completed_at
																	? `${Math.round(dayjs(instance.completed_at).diff(dayjs(instance.started_at), 'minute', true))}m`
																	: 'N/A'}
															</Text>
														</Descriptions.Item>
													</Descriptions>

													{/* Step Execution */}
													{instance.processing_group?.group_step_instances?.length > 0 && (
														<div>
															<Text style={smLabel}>Step Execution:</Text>
															<div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
																{instance.processing_group.group_step_instances
																	.sort((a: any, b: any) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime())
																	.map((stepInstance: any, index: number) => (
																		<div
																			key={stepInstance.id}
																			style={{
																				borderLeft: `3px solid ${getStepBorderColor(stepInstance.status)}`,
																				paddingLeft: 12,
																				paddingTop: 8,
																				paddingBottom: 8,
																				background: 'var(--mg-bg-subtle)',
																				borderRadius: '0 4px 4px 0',
																			}}
																		>
																			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
																				<div>
																					<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
																						{getStatusIcon(stepInstance.status)}
																						<Text style={smLabel}>Step {index + 1}</Text>
																						<StatusTag status={stepInstance.status} />
																					</div>
																					<Text style={secondaryText}>Step ID: {stepInstance.step_id}</Text>
																				</div>
																				<div style={{ textAlign: 'right' }}>
																					<Text style={{ ...secondaryText, display: 'block' }}>Started: {formatDate(stepInstance.started_at)}</Text>
																					<Text style={{ ...secondaryText, display: 'block' }}>Completed: {formatDate(stepInstance.completed_at)}</Text>
																					<Text style={{ ...secondaryText, display: 'block' }}>
																						Duration: {stepInstance.started_at && stepInstance.completed_at
																							? `${Math.round(dayjs(stepInstance.completed_at).diff(dayjs(stepInstance.started_at), 'minute', true))}m`
																							: 'N/A'}
																					</Text>
																				</div>
																			</div>

																			{stepInstance.field_values && Object.keys(stepInstance.field_values).length > 0 && (
																				<div style={{ marginTop: 8 }}>
																					<Text style={smLabel}>Field Values:</Text>
																					<Row gutter={[8, 4]} style={{ marginTop: 4 }}>
																						{Object.entries(stepInstance.field_values).map(([key, value]) => (
																							<Col key={key} span={12}>
																								<div style={{ display: 'flex', justifyContent: 'space-between' }}>
																									<Text style={{ ...secondaryText, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</Text>
																									<Text style={smLabel}>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</Text>
																								</div>
																							</Col>
																						))}
																					</Row>
																				</div>
																			)}

																			{stepInstance.notes && (
																				<div style={{ marginTop: 8 }}>
																					<Text style={smLabel}>Notes:</Text>
																					<Text style={{ ...secondaryText, display: 'block', marginTop: 4 }}>{stepInstance.notes}</Text>
																				</div>
																			)}

																			{stepInstance.attachments?.length > 0 && (
																				<div style={{ marginTop: 8 }}>
																					<Text style={smLabel}>Attachments:</Text>
																					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
																						{stepInstance.attachments.map((attachment: any, idx: number) => (
																							<Tag key={idx} style={{ borderColor: 'var(--mg-primary-border)', background: 'var(--mg-primary-bg)', color: 'var(--mg-primary)', fontSize: 11 }}>
																								Attachment {idx + 1}
																							</Tag>
																						))}
																					</div>
																				</div>
																			)}

																			<div style={{ marginTop: 8, paddingTop: 4, borderTop: '1px solid var(--mg-border)' }}>
																				<Text style={secondaryText}>Performed by User ID: {stepInstance.performed_by}</Text>
																			</div>
																		</div>
																	))}
															</div>
														</div>
													)}

													{/* Processing Group Results */}
													{instance.processing_group?.results?.length > 0 && (
														<div style={{ marginTop: 12 }}>
															<Text style={smLabel}>Processing Results:</Text>
															<div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
																{instance.processing_group.results.map((result: any) => (
																	<div key={result.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', border: '1px solid var(--mg-border)', borderRadius: 6 }}>
																		<div>
																			<Text style={smLabel}>{result.result_type} — {result.test_type}</Text>
																			<br />
																			<Text style={secondaryText}>Status: {result.status}{result.analyzed_at && ` • Analyzed: ${formatDate(result.analyzed_at)}`}</Text>
																		</div>
																		<Space>
																			{result.files?.length > 0 && (
																				<Tag style={{ borderColor: 'var(--mg-primary-border)', background: 'var(--mg-primary-bg)', color: 'var(--mg-primary)', fontSize: 11 }}>
																					{result.files.length} file(s)
																				</Tag>
																			)}
																			<StatusTag status={result.verification_status || 'pending'} />
																		</Space>
																	</div>
																))}
															</div>
														</div>
													)}

													{instance.metadata && (
														<div style={{ marginTop: 12 }}>
															<Text style={smLabel}>Metadata:</Text>
															<pre style={{ fontSize: 12, background: 'var(--mg-bg-subtle)', padding: 8, borderRadius: 4, marginTop: 4, overflowX: 'auto', border: '1px solid var(--mg-border)' }}>
																{JSON.stringify(instance.metadata, null, 2)}
															</pre>
														</div>
													)}
												</SectionCard>
											))}
										</div>
									</div>
								)}

								{/* Analysis Notes */}
								{analysis.notes && (
									<div>
										<Text style={{ fontSize: 13, fontWeight: 500 }}>Notes</Text>
										<SectionCard style={{ marginTop: 8 }}>
											<Text style={smText}>{analysis.notes}</Text>
										</SectionCard>
									</div>
								)}
							</div>
						</Collapse.Panel>
					))}
				</Collapse>
			</SectionCard>
		</div>
	);
};

export default AnalysisResultsTab;
